/** @format */

import React, { useState } from "react";
import { Typography, Box, Card, Button, Skeleton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const DynamicContentRenderer = ({ content, disruptCareerLoading }) => {
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (index) => {
    setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const renderCard = (text, index, title = null) => {
    // Ensure text is always a string
    const textString = typeof text === 'string' ? text : (typeof text === 'object' ? JSON.stringify(text) : String(text));
    const body = textString.trim();
    
    // Don't render if body is empty or just "[object Object]"
    if (!body || body === '[object Object]' || body === '{}') {
      return null;
    }
    
    const previewText = body.split(" ").slice(0, 30).join(" ") + "..."; // Show first 30 words
    const isExpandable = body.split(" ").length > 30;

    return (
      <Card
        key={index}
        sx={{
          marginBottom: 2,
          boxShadow: "none !important",
          background: "rgb(226 245 255)",
          padding: 2.5,
        }}
      >
        {title && (
          <Box display="flex" alignItems="flex-start" gap={1.5}>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{title}</Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: 14, fontWeight: 400, marginTop: title ? 1 : 0 }}>
          {expanded[index] ? body : previewText}
          {isExpandable && (
            <Button
              onClick={() => toggleExpand(index)}
              sx={{ fontSize: 13, p: 0, textTransform: "capitalize", display: 'flex', alignItems: 'center' }}
            >
              <ExpandMoreIcon
                sx={{
                  transform: expanded[index] ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "0.3s",
                  fontSize: 18,
                }}
              />
              {expanded[index] ? "Read Less" : "Read More"}
            </Button>
          )}
        </Typography>
      </Card>
    );
  };

  const renderObjectCard = (obj, index) => {
    // Handle trend objects with structured properties
    const trend = obj.trend || obj.title || obj.name || '';
    const description = obj.description || obj.content || obj.text || '';
    const impactOnRoles = obj.impactOnRoles || obj.impact || '';
    const requiredCompetencies = obj.requiredCompetencies || obj.competencies || obj.skills || [];
    const talentDemand = obj.talentDemand || obj.demand || '';
    
    // Build the full content
    let fullContent = '';
    if (trend) fullContent += `Trend: ${trend}\n\n`;
    if (description) fullContent += `Description: ${description}\n\n`;
    if (impactOnRoles) fullContent += `Impact on Roles: ${impactOnRoles}\n\n`;
    if (Array.isArray(requiredCompetencies) && requiredCompetencies.length > 0) {
      fullContent += `Required Competencies:\n${requiredCompetencies.map(c => `• ${c}`).join('\n')}\n\n`;
    }
    if (talentDemand) fullContent += `Talent Demand: ${talentDemand}`;
    
    // Trim trailing whitespace
    fullContent = fullContent.trim();
    
    const previewContent = description || trend || fullContent;
    const previewText = previewContent.split(" ").slice(0, 30).join(" ") + "...";
    const isExpandable = fullContent.split(" ").length > 30;

    return (
      <Card
        key={index}
        sx={{
          marginBottom: 2,
          boxShadow: "none !important",
          background: "rgb(226 245 255)",
          padding: 2.5,
        }}
      >
        {trend && (
          <Box display="flex" alignItems="flex-start" gap={1.5} sx={{ marginBottom: 1 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{trend}</Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: 14, fontWeight: 400, whiteSpace: 'pre-line' }}>
          {expanded[index] ? fullContent : previewText}
          {isExpandable && (
            <Button
              onClick={() => toggleExpand(index)}
              sx={{ fontSize: 13, p: 0, textTransform: "capitalize", mt: 1, display: 'flex', alignItems: 'center' }}
            >
              <ExpandMoreIcon
                sx={{
                  transform: expanded[index] ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "0.3s",
                  fontSize: 18,
                }}
              />
              {expanded[index] ? "Read Less" : "Read More"}
            </Button>
          )}
        </Typography>
      </Card>
    );
  };

  const parseContent = (data) => {
    // Debug logging    
    
    // Handle null, undefined, or empty data
    if (!data) {
      return null;
    }

    // Handle array format
    if (Array.isArray(data)) {
      if (data.length === 0) {
        return null;
      }
      return data.map((item, index) => {
        // If array item is a string, render it directly
        if (typeof item === 'string') {
          return renderCard(item, index);
        }
        // If array item is an object, render it with structured format
        if (typeof item === 'object' && item !== null) {
          // Check if it's a trend object with specific structure
          if (item.trend || item.description || item.impactOnRoles) {
            return renderObjectCard(item, index);
          }
          // Try common object properties that might contain the text
          const text = item.text || item.content || item.message || item.description || item.value || item.title;
          if (text && typeof text === 'string') {
            return renderCard(text, index);
          }
          // If no string property found, try to render as structured object
          return renderObjectCard(item, index);
        }
        // Fallback for other types
        return renderCard(String(item), index);
      }).filter(Boolean);
    }

    // Handle object format - try to extract array or string from common properties
    if (typeof data === 'object' && data !== null) {
      // Check if object has an array property
      if (Array.isArray(data.data)) {
        return parseContent(data.data);
      }
      if (Array.isArray(data.content)) {
        return parseContent(data.content);
      }
      if (Array.isArray(data.items)) {
        return parseContent(data.items);
      }
      if (Array.isArray(data.list)) {
        return parseContent(data.list);
      }
      
      // Check if object has a string property
      if (typeof data.content === 'string') {
        return parseContent(data.content);
      }
      if (typeof data.text === 'string') {
        return parseContent(data.text);
      }
      if (typeof data.data === 'string') {
        return parseContent(data.data);
      }
      
      // If object has multiple string properties, render them
      const stringValues = Object.values(data).filter(val => typeof val === 'string' && val.trim() !== '');
      if (stringValues.length > 0) {
        return stringValues.map((item, index) => renderCard(item, index));
      }
      
      // Check if object values contain arrays
      const arrayValues = Object.values(data).filter(val => Array.isArray(val));
      if (arrayValues.length > 0) {
        // Use the first array found
        return parseContent(arrayValues[0]);
      }
      
      // If we can't extract meaningful content, show a helpful message
      console.warn('Unable to extract content from object:', data);
      return renderCard("Unable to display content. Data format not recognized.", 0);
    }

    // Handle string format (existing logic)
    if (typeof data === 'string') {
      // Try to parse as JSON first
      if (data.trim().startsWith('[') || data.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(data);
          return parseContent(parsed);
        } catch (e) {
          // If parsing fails, treat as regular string
        }
      }
      
      if (data.trim() === '') {
        return null;
      }
      return data.split("\n\n").map((section, index) => {
        const lines = section.split("\n").filter((line) => line.trim() !== "");
        if (lines.length === 0) return null;
        
        const firstLine = lines[0];
        const idx = firstLine.indexOf(":");
        
        // If there's a colon, treat it as title:body format
        if (idx !== -1) {
          const title = firstLine.substring(0, idx + 1).trim();
          const body = firstLine.substring(idx + 1).trim();
          return renderCard(body, index, title);
        }
        
        // Otherwise, treat the whole section as body
        return renderCard(firstLine, index);
      }).filter(Boolean);
    }

    // Fallback: convert to string
    return renderCard(String(data), 0);
  };

  return (
    <Box sx={{ padding: 2, margin: "auto" }}>
      {disruptCareerLoading ? (
        // Show skeleton loaders when loading
        <>
          {[...Array(3)].map((_, i) => (
            <Card key={i} sx={{ marginBottom: 2, boxShadow: "0rem 0.25rem 0px #caedff", padding: 2.5 }}>
              <Skeleton variant="text" width="50%" height={20} />
              <Skeleton variant="text" width="90%" height={15} />
              <Skeleton variant="text" width="80%" height={15} />
              <Skeleton variant="rectangular" width="100%" height={60} sx={{ marginTop: 1 }} />
            </Card>
          ))}
        </>
      ) : (
        parseContent(content)
      )}
    </Box>
  );
};

export default DynamicContentRenderer;
